<script>
import { defineAsyncComponent, h } from 'vue'
import { useTheme } from 'vuetify'

const iconCache = new Map()
const iconModules = import.meta.glob('./*.vue')
const fallbackIcon = 'UnorderedList'

function getClass(text = '') {
  return text
    .trim()
    .replace(/(^\w|-\w)/g, t => t.replace('-', '').toUpperCase())
}

function getSlotText(slotContent) {
  if (!slotContent?.length) return ''
  const first = slotContent[0]
  if (typeof first?.children === 'string') return String(first.children).trim()
  if (typeof first?.children === 'number') return String(first.children).trim()
  if (Array.isArray(first?.children)) {
    for (const c of first.children) {
      if (typeof c === 'string') return String(c).trim()
      if (typeof c === 'number') return String(c).trim()
    }
  }
  return ''
}

function getIconComponent(iconClass) {
  if (iconCache.has(iconClass)) return iconCache.get(iconClass)

  const modulePath = `./${iconClass}.vue`
  const loader = iconModules[modulePath] || iconModules[`./${fallbackIcon}.vue`]
  const component = defineAsyncComponent(loader)

  iconCache.set(iconClass, component)
  return component
}

export default {
  name: 'Icon',
  props: {
    active: Boolean,
    dark: Boolean,
    error: Boolean, // color
    fill: String,
    height: {
      type: [Number, String],
      default: 30
    },
    info: Boolean,
    width: {
      type: [Number, String],
      default: 30
    },
    primary: Boolean, // color
    scaleStroke: Boolean,
    stroked: Boolean,
    strokeWidth: {
      type: String,
      default: '2'
    },
    success: Boolean,
    type: String,
    warning: Boolean,
    weight: String
  },
  setup(props, { slots }) {
    const theme = useTheme()

    const getFills = () => {
      const colors = theme.current.value.colors

      let defaultFill = ['#424242'] // grey-darken-3 fallback

      if (props.dark) defaultFill = ['#424242']
      if (props.error) defaultFill = [colors.error]
      if (props.primary) defaultFill = [colors.primary]
      if (props.success) defaultFill = [colors.success]
      if (props.warning) defaultFill = [colors.warning]
      if (props.info) defaultFill = [colors.info]

      if (!props.fill) return defaultFill

      const colorMap = {
        error: colors.error,
        info: colors.info,
        primary: colors.primary,
        success: colors.success,
        warning: colors.warning
      }

      return props.fill
        .split(/\s*,\s*/)
        .map(f => colorMap[f] || f)
    }

    return () => {
      const slotText = getSlotText(slots.default?.())
      const iconClass = slotText ? getClass(slotText) : fallbackIcon
      const IconComponent = getIconComponent(iconClass)

      return h(IconComponent, {
        active: props.active,
        fills: getFills(),
        height: String(props.height),
        primary: props.primary,
        scaleStroke: props.scaleStroke,
        strokeWidth: props.strokeWidth,
        stroked: props.stroked,
        type: iconClass,
        weight: props.weight,
        width: String(props.width)
      })
    }
  }
}
</script>
